import { Button, Flex, Tag, Tooltip } from 'antd'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { useFilterLabels } from 'src/context'
import { useQueryUpdate } from 'src/hooks'
import { defaultFilters, resolveActiveFilterTag } from 'src/utils'
import type { ActiveFilterConfig } from 'src/utils'

import { Text } from '../Text'

export type ActiveFilterTagsProps = {
  items: ActiveFilterConfig[]
  onClearAll?: () => void
  extra?: ReactNode
}

export const ActiveFilterTags = ({
  items,
  onClearAll,
  extra
}: ActiveFilterTagsProps) => {
  const { t, i18n } = useTranslation()
  const { params, updateQuery } = useQueryUpdate()
  const filterLabels = useFilterLabels()
  const locale = (i18n.resolvedLanguage ?? i18n.language ?? 'en')
    .toLowerCase()
    .split('-')[0]

  const clear = (item: { clearKeys: string[]; onClear?: () => void }) => {
    item.onClear?.()
    updateQuery({
      ...Object.fromEntries(item.clearKeys.map((key) => [key, undefined])),
      page: defaultFilters.page
    })
  }

  const tags = items.flatMap((config) => {
    const tag = resolveActiveFilterTag(
      config,
      params,
      filterLabels?.getLabel,
      locale
    )

    return tag ? [tag] : []
  })

  if (!tags.length && !extra) return null

  return (
    <Flex align="center" wrap gap={8}>
      <Text type="secondary" size={12}>
        {t('filtering_by')}
      </Text>

      {tags.map((item) => {
        const [first, ...rest] = item.values
        const overflowCount = rest.length

        const tag = (
          <Tag key={item.key} closable onClose={() => clear(item)}>
            <Text type="secondary" size={12}>
              {item.label}:
            </Text>{' '}
            <Text weight={500} size={12}>
              {first}
            </Text>
            {overflowCount > 0 && (
              <Text weight={500} size={12}>{` +${overflowCount}`}</Text>
            )}
          </Tag>
        )

        if (overflowCount === 0) return tag

        return (
          <Tooltip
            key={item.key}
            title={
              <Flex vertical gap={2}>
                {item.values.map((value, index) => (
                  <span key={index}>{value}</span>
                ))}
              </Flex>
            }
          >
            {tag}
          </Tooltip>
        )
      })}

      {extra}

      {onClearAll && (
        <Button
          type="link"
          size="small"
          icon={<X size={14} />}
          onClick={onClearAll}
        >
          {t('clear_all')}
        </Button>
      )}
    </Flex>
  )
}
